import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { DependencyType, type Item } from '@/lib/api/types/items';
import { getItemFetchQueryOptions } from '@/lib/queries/common/useGetItemFetchQuery';
import { useSuspenseGetItemStatusesQuery } from '@/lib/queries/common/useGetItemStatusesQuery';
import { useSuspenseItemTypesQuery } from '@/lib/queries/common/useItemTypesQuery';
import { useItemHealth } from '@/lib/queries/dashboard/useItemHealth';

const PANEL_QUERY_OPTIONS = {
  refetchOnMount: false,
} as const;

const useDependencyItems = (itemIds: number[]): { items: Item[]; isLoading: boolean } =>
  useQueries({
    queries: [...new Set(itemIds)].map((itemId) => ({
      ...getItemFetchQueryOptions({ itemId }),
      ...PANEL_QUERY_OPTIONS,
      placeholderData: (previousData: Item | undefined) => previousData,
    })),
    combine: (results) => ({
      items: results.map((result) => result.data).filter((item): item is Item => item != null),
      isLoading: results.some((result) => result.isLoading),
    }),
  });

export function useItemDependenciesPanelQuery(item: Item) {
  const { data: itemStatuses = [] } = useSuspenseGetItemStatusesQuery(PANEL_QUERY_OPTIONS);
  const { data: itemTypes = [] } = useSuspenseItemTypesQuery(PANEL_QUERY_OPTIONS);

  const predecessorsIds = useMemo(
    () =>
      item.dependencies
        .filter((dep) => dep.dependencyType === DependencyType.Predecessor)
        .map((dep) => dep.dependencyItemId),
    [item.dependencies],
  );
  const successorsIds = useMemo(
    () =>
      item.dependencies
        .filter((dep) => dep.dependencyType === DependencyType.Successor)
        .map((dep) => dep.dependencyItemId),
    [item.dependencies],
  );

  const dependencyItemIds = useMemo(
    () => [...predecessorsIds, ...successorsIds],
    [predecessorsIds, successorsIds],
  );

  // Fetched via the canonical per-item key (not the bulk-fetch endpoint) so that
  // any item edit elsewhere in the app — which always invalidates commonQueryKeys.item(id) —
  // refreshes this tab live instead of only after a page reload.
  const { items: dependencies, isLoading: isDependenciesLoading } =
    useDependencyItems(dependencyItemIds);

  const isPredecessorsLoading = predecessorsIds.length > 0 && isDependenciesLoading;
  const isSuccessorsLoading = successorsIds.length > 0 && isDependenciesLoading;

  useItemHealth({ itemIds: dependencyItemIds });

  const predecessors = useMemo(
    () => dependencies.filter((dep) => predecessorsIds.includes(dep.id)),
    [dependencies, predecessorsIds],
  );
  const successors = useMemo(
    () => dependencies.filter((dep) => successorsIds.includes(dep.id)),
    [dependencies, successorsIds],
  );

  return {
    dependencies,
    itemStatuses,
    itemTypes,
    predecessors,
    successors,
    isPredecessorsLoading,
    isSuccessorsLoading,
  };
}
